<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { sideConfig } from '@/lib/product'
import SideHeading from './SideHeading.vue'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import PriceHistoryChart from '@/components/PriceHistoryChart.vue'
import FilterChips from '@/components/FilterChips.vue'
import type { ChartResponse } from './catalogDetailTypes'

const { t } = useI18n()

const props = defineProps<{
    side: 'resell' | 'buyback'
    data: ChartResponse | null
    loading: boolean
    ownPriceLabel: string
}>()

/**
 * The selection belongs to a chart, not to the card: each currency has its own set of
 * countries, and narrowing one chart is no reason to narrow the chart beside it. Keyed
 * by currency, absent meaning every country.
 *
 * It is also per *item*, which this component cannot see: the parent keys each card on
 * the catalog item id, so a different item gets a different instance rather than
 * inheriting the country picked on the last one.
 */
const countryByCurrency = ref<Record<string, string | null>>({})

/**
 * One chart per currency the market traded this SKU in.
 *
 * The vendor series used to share a single axis whatever their currency, so a CHF
 * line and a EUR line were drawn as one comparison and the tooltip labelled both
 * with whichever symbol arrived first. Splitting by currency is the only reading
 * that means anything (ADR-002).
 */
const charts = computed(() => {
    const historical = props.data?.historical ?? []
    const currencies: string[] = []

    for (const point of historical) {
        if (!currencies.includes(point.currency)) {
            currencies.push(point.currency)
        }
    }

    return currencies.map((currency) => {
        const points = historical.filter((point) => point.currency === currency)
        const selected = countryByCurrency.value[currency] ?? null

        /** This currency's own countries — the chips are its coverage as much as its control. */
        const countries = [...new Set(points.map((point) => point.country))].filter(Boolean).sort()

        return {
            currency,
            currencySymbol: points[0]?.currency_symbol ?? '',
            // The currency sits in the control rather than beside it as a badge: it is
            // the same kind of fact as the country, and reading both in one place is what
            // makes "CHF, Switzerland only" a sentence. A chart plots exactly one
            // currency, so it always renders stated — which is also what keeps ADR-022's
            // "a chart in an unnamed currency is not a chart of anything".
            groups: [
                {
                    kind: 'currency',
                    label: t('catalog.details.competitorPrices.currency'),
                    options: [currency],
                },
                {
                    kind: 'country',
                    label: t('catalog.details.competitorPrices.country'),
                    options: countries,
                    uppercase: true,
                },
            ],
            filter: selected === null ? null : { kind: 'country', value: selected },
            // A country is a market of its own: one German vendor against three Italian
            // ones is a crowded chart in which the German line is the only one wanted.
            // Narrowing drops series rather than redrawing them, so what is left keeps
            // its exact shape.
            historical: selected === null ? points : points.filter((point) => point.country === selected),
            // A snapshot belongs to the item's one selling currency (ADR-004), so it
            // overlays that chart alone. On every other currency's chart there is no
            // comparable number of the customer's to draw — and a price-less item,
            // whose snapshots carry a null currency, matches none of them.
            //
            // It survives the country filter: the customer's own price belongs to no
            // country, and it is the line the market is being read against.
            snapshots: (props.data?.snapshots ?? []).filter((snapshot) => snapshot.currency === currency),
        }
    })
})

const accent = computed(() => sideConfig[props.side].accent)

/** Names the side itself, so the card needs no heading above it to say which it is. */
const title = computed(() =>
    props.side === 'resell'
        ? t('catalog.details.pricesHistoricalResell')
        : t('catalog.details.pricesHistoricalBuyback'),
)
</script>

<template>
    <Card class="border-l-4" :class="accent.border">
        <!--
            The title lives on the card rather than on a heading above it: two chart cards
            a scroll apart are otherwise told apart by a 4px border, and reading a trade-in
            chart as a resell one is a costly mistake.
        -->
        <CardHeader class="flex flex-row flex-wrap items-center gap-x-4 gap-y-2 space-y-0 py-4">
            <SideHeading :side="side" :title="title" size="sm" class="w-full" />
        </CardHeader>

        <CardContent>
            <template v-if="loading">
                <Skeleton class="h-48 w-full" />
            </template>
            <template v-else-if="charts.length === 0">
                <p class="text-sm text-muted-foreground py-8 text-center">
                    {{ t('catalog.details.marketPricesEmpty') }}
                </p>
            </template>
            <template v-else>
                <div class="space-y-8">
                    <div v-for="chart in charts" :key="chart.currency">
                        <!--
                            Above the chart it affects rather than in the card header: the
                            chips list the countries that traded in *this* currency, and
                            picking one leaves the chart beside it untouched.
                        -->
                        <FilterChips
                            :model-value="chart.filter"
                            :groups="chart.groups"
                            class="mb-2"
                            @update:model-value="countryByCurrency[chart.currency] = $event?.value ?? null"
                        />
                        <PriceHistoryChart
                            :historical="chart.historical"
                            :snapshots="chart.snapshots"
                            :currency-symbol="chart.currencySymbol"
                            :own-price-label="ownPriceLabel"
                        />
                    </div>
                </div>
            </template>
        </CardContent>
    </Card>
</template>
